import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Text, Flex, HStack } from "@chakra-ui/react";
import { $Routes } from "@routes";
import { EngagementBadge, RemixBadge, AgendaActionMenu } from "@components";
import { useExportAgendaPptx } from "@hooks";
import { ImpactTag } from "@components/Reports";
import {
  getMeetingTypeIcon,
  getMeetingTypeColor,
} from "@utils/meetingTypeIcons";

const AgendaListItem = ({
  id,
  index,
  title,
  live,
  isPastAgenda,
  setAgendaId,
  agendaLength,
  setAgendaDeleteModalOpen,
  setAgendaDuplicateModalOpen,
  onMenuToggle,
  // New props for enhanced design
  eScore,
  isRemixed = false,
  meetingType, // New prop for meeting type (e.g., 'team', 'company', 'training')
  blueprint, // Blueprint data object
  onBlueprintOpen, // Function to open blueprint modal
  onRemix, // Function to handle agenda remix
  onRename, // Function to handle agenda rename
  impactScore, // Impact score for Reports page
  navigateToReports = false, // New prop to control navigation behavior
  hideTitle = false, // New prop to hide title
  hideMenu = false, // New prop to hide menu button
}) => {
  const navigate = useNavigate();
  const { exportAgenda, isExporting: isExportingPptx } = useExportAgendaPptx();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [menuPosition, setMenuPosition] = React.useState({ x: 0, y: 0 });
  const menuButtonRef = React.useRef(null);

  const handleDeleteAgenda = (id) => {
    setAgendaDeleteModalOpen(true);
    setAgendaId(id);
  };

  const handleDuplicateAgenda = (id) => {
    setAgendaDuplicateModalOpen(true);
    setAgendaId(id);
  };

  const handleMoreOptionsClick = React.useCallback(
    (event) => {
      event.stopPropagation();

      if (!isMenuOpen) {
        // Calculate dropdown position like ActivityCardContainer does
        const rect = event.currentTarget.getBoundingClientRect();
        const position = {
          x: rect.right - 133, // Align dropdown right edge with button right edge
          y: rect.bottom + 4, // Position below button with small gap
        };
        setMenuPosition(position);
        setIsMenuOpen(true);
        if (onMenuToggle) onMenuToggle(true);
      }
    },
    [isMenuOpen, onMenuToggle]
  );

  const handleMenuClose = React.useCallback(() => {
    setIsMenuOpen(false);
    if (onMenuToggle) onMenuToggle(false);
  }, [onMenuToggle]);

  const handleCardClick = () => {
    if (isPastAgenda && navigateToReports) {
      // Only navigate to reports if explicitly told to do so
      navigate(`${$Routes.reportDetailPage}/${id}`);
    } else {
      // Default behavior: navigate to agenda detail page
      navigate(`${$Routes.agendaDetailPage}/${id}`);
    }
  };

  // Get the appropriate icon and color for this meeting type
  const IconComponent = getMeetingTypeIcon(meetingType);
  const iconColor = getMeetingTypeColor(meetingType);

  return (
    <>
      <Box
        data-testid={`agenda-list-item-${id}`}
        bg="white"
        borderRadius="8px"
        boxShadow="0px 2px 4px rgba(12, 0, 28, 0.04), 0px 0px 6px rgba(12, 0, 28, 0.02)"
        p="12px"
        cursor="pointer"
        transition="all 0.2s"
        _hover={{
          transform: "translateY(-1px)",
          boxShadow:
            "0px 4px 8px rgba(12, 0, 28, 0.08), 0px 0px 12px rgba(12, 0, 28, 0.04)",
        }}
        onClick={handleCardClick}
        position="relative"
      >
        <Flex align="center" gap="12px">
          {/* Agenda Type Icon */}
          <Box
            w="24px"
            h="24px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <IconComponent size="24px" color={iconColor} strokeWidth="1.5px" />
          </Box>

          {/* Title and Content */}
          <Flex direction="column" flex="1" gap="5px" minW="0">
            {!hideTitle && (
              <Text
                fontSize="12px"
                fontWeight="400"
                color="#000000"
                lineHeight="1.5"
                letterSpacing="-1%"
                fontFamily="Geist, sans-serif"
                isTruncated
              >
                {title}
              </Text>
            )}

            {/* Show ImpactTag immediately when title is hidden */}
            {hideTitle && isPastAgenda && impactScore !== undefined && (
              <HStack spacing="8px" align="center">
                <ImpactTag impactScore={impactScore} />
              </HStack>
            )}

            {/* Remixed Badge only - engagement goes to the right */}
            {!isPastAgenda && isRemixed && (
              <HStack spacing="8px" align="center">
                <RemixBadge visible={true} />
              </HStack>
            )}
            {/* Show ImpactTag below title when title is shown */}
            {!hideTitle && isPastAgenda && impactScore !== undefined && (
              <HStack spacing="8px" align="center">
                <ImpactTag impactScore={impactScore} />
              </HStack>
            )}
          </Flex>

          {/* Right Side Actions */}
          <HStack spacing="6px" align="center">
            {/* Engagement Badge */}
            {isPastAgenda && impactScore !== undefined && (
              <EngagementBadge percentage={impactScore} />
            )}

            {/* Active Badge for live agendas */}
            {!isPastAgenda && live && (
              <Box
                bg="#10B981"
                borderRadius="6px"
                px="8px"
                py="4px"
                display="flex"
                alignItems="center"
                gap="4px"
                animation="pulse 2s infinite"
                sx={{
                  "@keyframes pulse": {
                    "0%, 100%": {
                      opacity: 1,
                    },
                    "50%": {
                      opacity: 0.8,
                    },
                  },
                }}
              >
                <Text
                  fontSize="10px"
                  fontWeight="500"
                  color="white"
                  lineHeight="1.2"
                  letterSpacing="0.5px"
                  fontFamily="Geist, sans-serif"
                  textTransform="uppercase"
                >
                  ⚡ LIVE
                </Text>
              </Box>
            )}

            {/* More Menu Button - Now with custom dropdown */}
            {!hideMenu && (
              <AgendaActionMenu
                isOpen={isMenuOpen}
                onClose={handleMenuClose}
                position={menuPosition}
                anchorRef={menuButtonRef}
                onMoreOptionsClick={handleMoreOptionsClick}
                blueprintDisabled={!blueprint}
                isPastAgenda={isPastAgenda}
                onEdit={() => {
                  navigate(`${$Routes.agendaDetailPage}/${id}`);
                }}
                onRemix={() => {
                  if (onRemix) {
                    // Pass the entire agenda object for the remix modal
                    const agendaData = {
                      _id: id,
                      title,
                      blueprint,
                      isRemixed,
                      meetingType,
                      live,
                      eScore,
                    };
                    onRemix(agendaData);
                  }
                }}
                onRename={() => {
                  if (onRename) {
                    const agendaData = {
                      _id: id,
                      title,
                    };
                    onRename(agendaData);
                  }
                }}
                onBlueprint={() => {
                  console.log(
                    "Blueprint clicked in AgendaListItem, blueprint:",
                    blueprint
                  );
                  console.log("Blueprint disabled:", !blueprint);
                  if (blueprint && onBlueprintOpen) {
                    onBlueprintOpen(blueprint);
                  }
                }}
                onExportPptx={() => exportAgenda(id)}
                isExportingPptx={isExportingPptx}
                onDuplicate={() => {
                  handleDuplicateAgenda(id);
                }}
                onDelete={
                  isPastAgenda
                    ? undefined
                    : () => {
                        handleDeleteAgenda(id);
                      }
                }
              />
            )}
          </HStack>
        </Flex>
      </Box>
    </>
  );
};

export default AgendaListItem;
